import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  HostListener
} from '@angular/core';
import { slideDownAnimation } from '../ticket-animations';

/**
 * Interface for milestone data
 */
export interface Milestone {
  id?: string | number;
  name: string;
  status?: string;
}

/**
 * TicketHeaderComponent
 *
 * Header component for ticket dialog/drawer matching task prototype design.
 * Features:
 * - Title with status badge
 * - Module and reference info display
 * - Action buttons (billable toggle, priority toggle, mark complete, PDF, edit, delete, close)
 * - Animated search bar
 * - Keyboard shortcuts (Ctrl+S for save)
 *
 * @selector app-ticket-header
 * @changeDetection OnPush
 */
@Component({
    selector: 'app-ticket-header',
    templateUrl: './ticket-header.component.html',
    styleUrls: ['./ticket-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [slideDownAnimation],
    standalone: false
})
export class TicketHeaderComponent implements OnInit, OnDestroy {

  // ============================================================================
  // INPUT PROPERTIES
  // ============================================================================

  /**
   * Mode: create, edit, or view
   */
  @Input() mode: 'create' | 'edit' | 'view' = 'create';

  /**
   * Ticket title from user input
   */
  @Input() ticketTitle: string = '';

  /**
   * Module label (e.g., 'Job', 'Proposal')
   */
  @Input() moduleLabel: string = 'Module';

  /**
   * Linked record's display name (e.g. the bid/job name a ticket references).
   */
  @Input() projectName: string = '';

  /**
   * Linked record's id, shown as "#id" next to projectName.
   */
  @Input() projectId: string | number = '';

  /**
   * Route to the linked record's detail page, or null when its module isn't
   * routable (see view-ticket-detail's getRecordRoute()) — falls back to
   * plain, non-clickable text.
   */
  @Input() recordRoute: string | null = null;

  /**
   * Current ticket status
   */
  @Input() status: string = '';

  /**
   * Status text to display in badge
   */
  @Input() statusText: string = '';

  /**
   * Status color from API
   */
  @Input() statusColor: string = '#6b7280';

  /**
   * Available statuses list
   */
  @Input() statuses: any[] = [];

  /**
   * Currently selected status object with color and lock info
   */
  @Input() selectedStatus: any = null;

  /**
   * Associated milestone information
   */
  @Input() milestone?: Milestone;

  /**
   * Fullscreen mode state
   */
  @Input() isFullscreen: boolean = false;

  /**
   * Form validity state
   */
  @Input() isFormValid: boolean = true;

  /**
   * Search query value
   */
  @Input() searchQuery: string = '';

  /**
   * Billable flag
   */
  @Input() isBillable: boolean = false;

  /**
   * High priority flag
   */
  @Input() isHighPriority: boolean = false;

  /**
   * Numeric priority value (1=Low, 2=Medium, 3=High, 4=Urgent)
   */
  @Input() priority: number = 2;

  /**
   * Previous priority before high toggle (for toggle back)
   */
  @Input() previousPriority: number = 2;

  /**
   * Show edit button (view mode)
   */
  @Input() showEdit: boolean = false;

  /**
   * Show delete button
   */
  @Input() showDelete: boolean = false;

  /**
   * Show mark complete button (view mode)
   */
  @Input() showMarkComplete: boolean = false;

  /**
   * Mark complete permission string
   */
  @Input() markCompletePermission: string = 'DONOTAPPLY';

  /**
   * Show new ticket button (view mode)
   */
  @Input() showNewTicket: boolean = false;

  /**
   * New ticket permission string
   */
  @Input() newTicketPermission: string = 'DONOTAPPLY';

  /**
   * Show PDF export button (view mode)
   */
  @Input() showPdfExport: boolean = false;

  /**
   * Show copy button (view mode)
   */
  @Input() showCopy: boolean = false;

  /**
   * PDF export permission string
   */
  @Input() pdfExportPermission: string = 'DONOTAPPLY';

  /**
   * Copy permission string
   */
  @Input() copyPermission: string = 'DONOTAPPLY';

  /**
   * Edit permission string
   */
  @Input() editPermission: string = 'DONOTAPPLY';

  /**
   * Delete permission string
   */
  @Input() deletePermission: string = 'DONOTAPPLY';

  /**
   * Create permission string
   */
  @Input() createPermission: string = 'DONOTAPPLY';

  /**
   * Status change permission string
   */
  @Input() statusChangePermission: string = 'DONOTAPPLY';

  /**
   * Lock state - when true, actions are disabled
   */
  @Input() isLocked: boolean = false;

  // ============================================================================
  // OUTPUT EVENTS
  // ============================================================================

  /**
   * Emits when search icon is clicked
   */
  @Output() searchToggle = new EventEmitter<void>();

  /**
   * Emits search query changes
   */
  @Output() searchChange = new EventEmitter<string>();

  /**
   * Emits when fullscreen is toggled
   */
  @Output() fullscreenToggle = new EventEmitter<void>();

  /**
   * Emits when save button is clicked
   */
  @Output() save = new EventEmitter<void>();

  /**
   * Emits when close button is clicked
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits when the linked-record name/id link is clicked, so the caller can
   * close this drawer before routing to the record's detail page.
   */
  @Output() recordLinkClick = new EventEmitter<void>();

  /**
   * Emits when delete button is clicked
   */
  @Output() delete = new EventEmitter<void>();

  /**
   * Emits when edit button is clicked (view mode)
   */
  @Output() edit = new EventEmitter<void>();

  /**
   * Emits when priority toggle button is clicked
   */
  @Output() priorityToggle = new EventEmitter<void>();

  /**
   * Emits when billable toggle button is clicked
   */
  @Output() billableToggle = new EventEmitter<void>();

  /**
   * Emits when mark complete is clicked
   */
  @Output() markComplete = new EventEmitter<void>();

  /**
   * Emits when PDF export is clicked
   */
  @Output() exportPDF = new EventEmitter<void>();

  /**
   * Emits when copy is clicked
   */
  @Output() copy = new EventEmitter<void>();

  /**
   * Emits when new ticket button is clicked (view mode)
   */
  @Output() newTicket = new EventEmitter<void>();

  /**
   * Emits when status select is clicked
   */
  @Output() statusSelect = new EventEmitter<any>();

  /**
   * Emits when PDF export button is clicked (alias for compatibility)
   */
  @Output() pdfExport = new EventEmitter<void>();

  /**
   * Emits when lock toggle is clicked
   */
  @Output() lockToggle = new EventEmitter<void>();

  // ============================================================================
  // VIEW CHILDREN
  // ============================================================================

  @ViewChild('searchInput', { static: false }) searchInput?: ElementRef;

  // ============================================================================
  // PUBLIC PROPERTIES
  // ============================================================================

  /**
   * Search bar visibility state
   */
  public showSearch: boolean = false;

  /**
   * Search input focus state
   */
  public isSearchFocused: boolean = false;

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    // Component initialization
  }

  ngOnDestroy(): void {
    // Cleanup
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Toggle search bar visibility
   */
  toggleSearch(): void {
    this.showSearch = !this.showSearch;
    this.searchToggle.emit();

    if (this.showSearch) {
      // Focus input after animation
      setTimeout(() => {
        this.searchInput?.nativeElement.focus();
      }, 200);
    }
  }

  /**
   * Handle search input changes
   */
  onSearchChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchChange.emit(value);
  }

  /**
   * Handle search input focus
   */
  onSearchFocus(): void {
    this.isSearchFocused = true;
  }

  /**
   * Handle search input blur
   */
  onSearchBlur(): void {
    this.isSearchFocused = false;
  }

  /**
   * Toggle fullscreen mode
   */
  toggleFullscreen(): void {
    this.fullscreenToggle.emit();
  }

  /**
   * Handle save button click
   */
  onSave(): void {
    if (this.isFormValid) {
      this.save.emit();
    }
  }

  /**
   * Handle close button click
   */
  onClose(): void {
    this.close.emit();
  }

  /**
   * Handle delete button click
   */
  onDelete(): void {
    this.delete.emit();
  }

  /**
   * Handle edit button click
   */
  onEdit(): void {
    this.edit.emit();
  }

  /**
   * Handle fullscreen toggle button click
   */
  onFullscreenToggle(): void {
    this.fullscreenToggle.emit();
  }

  /**
   * Toggle priority (fire icon)
   */
  onPriorityToggle(): void {
    this.priorityToggle.emit();
  }

  /**
   * Toggle billable (payment icon)
   */
  onBillableToggle(): void {
    this.billableToggle.emit();
  }

  /**
   * Handle mark complete
   */
  onMarkComplete(): void {
    this.markComplete.emit();
  }

  /**
   * Handle PDF export
   */
  onExportPDF(): void {
    this.exportPDF.emit();
    this.pdfExport.emit(); // Emit both for compatibility
  }

  /**
   * Handle copy click
   */
  onCopy(): void {
    if (this.isLocked) {
      return;
    }
    this.copy.emit();
  }

  /**
   * Handle new ticket button click
   */
  onNewTicket(): void {
    this.newTicket.emit();
  }

  /**
   * Handle lock toggle button click
   */
  onLockToggle(): void {
    this.lockToggle.emit();
  }

  /**
   * Handle status selection
   */
  onStatusSelect(status: any): void {
    this.statusSelect.emit(status);
  }

  /**
   * Keyboard shortcut handler (Ctrl+S for save)
   */
  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // Ctrl+S or Cmd+S for save
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      if (this.mode !== 'view') {
        this.onSave();
      }
    }

    // Escape to close search
    if (event.key === 'Escape' && this.showSearch) {
      this.toggleSearch();
    }
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Get formatted status text for display
   * Always uses current status input to reflect real-time changes
   * @returns Formatted status string (UPPERCASE)
   */
  public getFormattedStatus(): string {
    // Use selectedStatus object if available (contains correct name)
    if (this.selectedStatus && this.selectedStatus.name) {
      return this.selectedStatus.name.replace('-', ' ').toUpperCase();
    }

    // Fallback to status string (for backward compatibility)
    if (!this.status || typeof this.status !== 'string') {
      return 'NEW';
    }
    return this.status.replace('-', ' ').toUpperCase();
  }

  /**
   * Check if selected status is locked
   * Returns true if the status has lock_status = true
   * @returns Boolean indicating if status is locked
   */
  public getSelectedStatusLocked(): boolean {
    // Check if selectedStatus has lock_status (handles both true and 1)
    if (this.selectedStatus && (this.selectedStatus.lock_status === true || this.selectedStatus.lock_status === 1)) {
      return true;
    }

    // Fallback: Try to find status in statuses array by status string
    if (this.statuses && this.statuses.length > 0 && this.status) {
      const normalizedStatus = this.status.toLowerCase().trim();
      const statusObj = this.statuses.find((s: any) =>
        s.name?.toLowerCase().trim() === normalizedStatus
      );
      if (statusObj && (statusObj.lock_status === true || statusObj.lock_status === 1)) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get priority icon color
   */
  getPriorityIconColor(): string {
    if (this.priority === 3 || this.priority === 4 || this.isHighPriority) {
      return '#ef4444'; // High/Urgent = red
    }
    return '#6b7280'; // Default gray
  }

  /**
   * Get display title
   */
  getDisplayTitle(): string {
    if (this.mode === 'view') {
      return this.ticketTitle || 'Untitled Ticket';
    }
    return this.ticketTitle || (this.mode === 'create' ? 'New Ticket' : 'Edit Ticket');
  }

}
